import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../api';
import { formatISTDateOnly } from '../utils/formatDate';
import { useSearchParams } from 'react-router-dom';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  go: 'bg-blue-100 text-blue-700',
  running: 'bg-green-100 text-green-700',
  stopped: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-gray-100 text-gray-400',
  failed: 'bg-red-100 text-red-700',
};

export default function AdsStatus() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client') || '');
  const [ads, setAds] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const isLiveToday = (a) => {
    const started = !a.start_date || a.start_date.slice(0, 10) <= today;
    const notEnded = !a.end_date || a.end_date.slice(0, 10) >= today;
    return started && notEnded && a.status !== 'archived';
  };
  const liveAds = ads.filter(isLiveToday);
  const oldAds = ads.filter((a) => !isLiveToday(a));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [reportView, setReportView] = useState(null); // holds report card data, or null = show table
  const [runningId, setRunningId] = useState(null); // idea id currently sending, for button spinner
  const [togglingId, setTogglingId] = useState(null);

  const handleToggle = async (e, adId, action, source) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (source === 'platform') {
      const ok = window.confirm(
        `⚠️ This campaign was DISCOVERED from ${adId ? 'the platform' : ''} — it was not created through this dashboard. Are you sure you want to ${action} it?`
      );
      if (!ok) return;
    }
    setError('');
    setTogglingId(adId);
    try {
      await api.toggleStatus(adId, action);
      const updated = await api.getAds(selectedClient || undefined);
      setAds(updated);
      if (reportView && reportView.id === adId) {
        setReportView(null); // close the stale card; user can Run Report again for fresh data
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    api.getClients().then(setClients).catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    const fetchAds = (showLoading) => {
      if (showLoading) setLoading(true);
      api.getAds(selectedClient || undefined)
        .then(setAds)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    };

    fetchAds(true);
    const interval = setInterval(() => {
      if (!reportView) fetchAds(false);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [selectedClient, reportView]);

  const handleRunReport = async (e, adId) => {
    e.preventDefault(); // don't follow the row's Link
    e.stopPropagation();
    setError('');
    setRunningId(adId);
    try {
      const data = await api.runReport(adId);
      setReportView(Array.isArray(data) ? data[0] : data);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunningId(null);
    }
  };

  const closeReport = () => setReportView(null);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Ads Status</h1>
        {!reportView && (
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 min-w-[200px]"
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name}</option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2 mb-4">{error}</div>}

      {reportView ? (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={closeReport}
            className="text-sm text-brand-purple hover:underline mb-4"
          >
            &larr; Back to list
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
            <div className="bg-white rounded-xl shadow p-5 col-span-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase">Ad Idea #{reportView.id}</p>
                  <h2 className="text-lg font-bold text-gray-800">{reportView.headline}</h2>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${STATUS_COLORS[reportView.status] || 'bg-gray-100 text-gray-500'}`}>
                  {reportView.status}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">{reportView.client_name} — {reportView.brand} — {reportView.platform?.toUpperCase()}</p>
              {reportView.idea_description && (
                <p className="text-sm text-gray-600 mt-3 border-t border-gray-100 pt-3">{reportView.idea_description}</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Live Status</p>
              <p className="text-xl font-bold text-gray-800">{reportView.live_status || '—'}</p>
              <p className="text-xs text-gray-500 mt-1">Objective: {reportView.objective}</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Spend</p>
              <p className="text-2xl font-bold text-gray-800">₹{Number(reportView.live_spend ?? 0).toFixed(2)}</p>
              {reportView.pct !== null && reportView.pct !== undefined && (
                <p className="text-xs text-gray-500 mt-1">{reportView.pct}% of ₹{reportView.daily_budget} daily cap</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Impressions / Clicks</p>
              <p className="text-2xl font-bold text-gray-800">{reportView.live_impressions ?? 0} / {reportView.live_clicks ?? 0}</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Target CPA / ROAS</p>
              <p className="text-xl font-bold text-gray-800">
                {reportView.target_cpa ? `₹${reportView.target_cpa}` : '—'} / {reportView.target_roas || '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Lifetime Cap: {reportView.lifetime_cap ? `₹${reportView.lifetime_cap}` : '—'}</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Flight Dates</p>
              <p className="text-sm font-bold text-gray-800">
                {formatISTDateOnly(reportView.start_date)} → {formatISTDateOnly(reportView.end_date)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {reportView.daysLeft !== null && reportView.daysLeft !== undefined
                  ? (reportView.daysLeft >= 0 ? `${reportView.daysLeft} day(s) left` : `Ended ${Math.abs(reportView.daysLeft)} day(s) ago`)
                  : '—'}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-400 uppercase mb-1">Creative</p>
              <p className="text-sm font-bold text-gray-800">{reportView.creative_source}</p>
              <p className="text-xs text-gray-500 mt-1">Status: {reportView.creative_status}</p>
            </div>

            {reportView.creative_copy && (
              <div className="bg-white rounded-xl shadow p-5 col-span-3">
                <p className="text-xs text-gray-400 uppercase mb-1">Ad Copy</p>
                <p className="text-sm text-gray-700 italic whitespace-pre-line">{reportView.creative_copy}</p>
              </div>
            )}

            <div className="col-span-3 flex gap-3">
              {reportView.manage_url && (
                  <a
                  href={reportView.manage_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 text-center bg-brand-purple text-white rounded-xl py-3 font-medium hover:bg-brand-purpleDark"
                >
                  Manage Campaign →
                </a>
              )}
              {reportView.live_status !== 'PAUSED' ? (
                <button
                  onClick={(e) => handleToggle(e, reportView.id, 'pause', reportView.source)}
                  disabled={togglingId === reportView.id}
                  className="flex-1 bg-yellow-500 text-white rounded-xl py-3 font-medium hover:bg-yellow-600 disabled:opacity-50"
                >
                  ⏸ Pause
                </button>
              ) : (
                <button
                  onClick={(e) => handleToggle(e, reportView.id, 'enable', reportView.source)}
                  disabled={togglingId === reportView.id}
                  className="flex-1 bg-green-600 text-white rounded-xl py-3 font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  ▶ Enable
                </button>
              )}
            </div>

            <div className="bg-green-50 rounded-xl p-4 col-span-3 text-sm text-green-700">
              ✅ Sent to Telegram{reportView.email_sent ? ' and Email' : ''} just now.
            </div>
          </div>
        </div>
      ) : loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Live Today</h2>
          <div className="bg-white rounded-xl shadow overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3">Headline</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Daily Budget</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Creative</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {liveAds.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100 hover:bg-brand-hero">
                    <td className="px-0 py-0" colSpan="8">
                      <Link to={`/ads/${a.id}`} className="grid grid-cols-8 px-0 py-0 items-center w-full">
                        <span className="px-4 py-3 font-medium text-gray-800 col-span-1">{a.headline}</span>
                        <span className="px-4 py-3 text-gray-600 col-span-1">{a.brand}</span>
                        <span className="px-4 py-3 text-gray-600 capitalize col-span-1">{a.platform}</span>
                        <span className="px-4 py-3 text-gray-600 col-span-1">₹{a.daily_budget}</span>
                        <span className="px-4 py-3 text-gray-500 text-xs col-span-1">{formatISTDateOnly(a.start_date)}</span>
                        <span className="px-4 py-3 text-gray-500 text-xs col-span-1">{formatISTDateOnly(a.end_date)}</span>
                        <span className="px-4 py-3 col-span-1">
                          <span className={`px-2 py-1 rounded-full text-xs ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-500'}`}>
                            {a.status}
                          </span>
                        </span>
                        <span className="px-4 py-3 col-span-1">
                          <span className={`px-2 py-1 rounded-full text-xs ${a.creative_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {a.creative_status}
                          </span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {a.status === 'running' && (
                          <button onClick={(e) => handleToggle(e, a.id, 'pause', a.source)} disabled={togglingId === a.id}
                            className="text-xs bg-yellow-500 text-white px-3 py-1.5 rounded-lg hover:bg-yellow-600 disabled:opacity-50 whitespace-nowrap">
                            ⏸ Pause
                          </button>
                        )}
                        {a.status === 'stopped' && (
                          <button onClick={(e) => handleToggle(e, a.id, 'enable', a.source)} disabled={togglingId === a.id}
                            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap">
                            ▶ Enable
                          </button>
                        )}
                        <button onClick={(e) => handleRunReport(e, a.id)} disabled={runningId === a.id}
                          className="text-xs bg-brand-purple text-white px-3 py-1.5 rounded-lg hover:bg-brand-purpleDark disabled:opacity-50 whitespace-nowrap">
                          {runningId === a.id ? '...' : '📩 Report'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {liveAds.length === 0 && (
                  <tr><td colSpan="9" className="px-4 py-6 text-center text-gray-400">No ads live today.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="text-lg font-bold text-gray-800 mb-3">Older / Upcoming Ads</h2>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3">Headline</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Daily Budget</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Creative</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {oldAds.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100 hover:bg-brand-hero">
                    <td className="px-0 py-0" colSpan="8">
                      <Link to={`/ads/${a.id}`} className="grid grid-cols-8 px-0 py-0 items-center w-full">
                        <span className="px-4 py-3 font-medium text-gray-800 col-span-1">{a.headline}</span>
                        <span className="px-4 py-3 text-gray-600 col-span-1">{a.brand}</span>
                        <span className="px-4 py-3 text-gray-600 capitalize col-span-1">{a.platform}</span>
                        <span className="px-4 py-3 text-gray-600 col-span-1">₹{a.daily_budget}</span>
                        <span className="px-4 py-3 text-gray-500 text-xs col-span-1">{formatISTDateOnly(a.start_date)}</span>
                        <span className="px-4 py-3 text-gray-500 text-xs col-span-1">{formatISTDateOnly(a.end_date)}</span>
                        <span className="px-4 py-3 col-span-1">
                          <span className={`px-2 py-1 rounded-full text-xs ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-500'}`}>
                            {a.status}
                          </span>
                        </span>
                        <span className="px-4 py-3 col-span-1">
                          <span className={`px-2 py-1 rounded-full text-xs ${a.creative_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {a.creative_status}
                          </span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {a.status === 'running' && (
                          <button onClick={(e) => handleToggle(e, a.id, 'pause', a.source)} disabled={togglingId === a.id}
                            className="text-xs bg-yellow-500 text-white px-3 py-1.5 rounded-lg hover:bg-yellow-600 disabled:opacity-50 whitespace-nowrap">
                            ⏸ Pause
                          </button>
                        )}
                        {a.status === 'stopped' && (
                          <button onClick={(e) => handleToggle(e, a.id, 'enable', a.source)} disabled={togglingId === a.id}
                            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap">
                            ▶ Enable
                          </button>
                        )}
                        <button onClick={(e) => handleRunReport(e, a.id)} disabled={runningId === a.id}
                          className="text-xs bg-brand-purple text-white px-3 py-1.5 rounded-lg hover:bg-brand-purpleDark disabled:opacity-50 whitespace-nowrap">
                          {runningId === a.id ? '...' : '📩 Report'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {oldAds.length === 0 && (
                  <tr><td colSpan="9" className="px-4 py-6 text-center text-gray-400">No older or upcoming ads.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
}
